<script setup>
import { useLayout } from '@/layout/composables/layout';
import { onBeforeMount, ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { canOpen } from '@/navigation/access';

const route = useRoute();
const store = useStore();

const { layoutState, setActiveMenuItem, toggleMenuItem, isMenuItemExpanded, toggleMenu } = useLayout();

const props = defineProps({
    item: {
        type: Object,
        default: () => ({})
    },
    index: {
        type: Number,
        default: 0
    },
    root: {
        type: Boolean,
        default: true
    },
    parentItemKey: {
        type: String,
        default: null
    }
});

const isActiveMenu = ref(false);
const itemKey = ref(null);

const badgeCount = computed(() => {
    if (props.item.badgeKey) {
        return store.getters['navigation/' + props.item.badgeKey] || 0;
    }
    return 0;
});

onBeforeMount(() => {
    // The path of labels, e.g. "accounting.accounting/accounting.configuration".
    // Open folders are stored by this key (see layout.js), so it must not change
    // when entries are added to or moved in src/navigation/index.js.
    const name = props.item.label || String(props.index);
    itemKey.value = props.parentItemKey ? props.parentItemKey + '/' + name : name;
    // Check if this item should be expanded based on the current route or stored state
    isActiveMenu.value = isMenuItemExpanded(itemKey.value);
});

// Watch for changes in expanded items
watch(
    () => layoutState.expandedMenuItems,
    () => {
        isActiveMenu.value = isMenuItemExpanded(itemKey.value);
    },
    { deep: true }
);

function itemClick(event, item) {
    if (item.disabled) {
        event.preventDefault();
        return;
    }

    if ((item.to || item.url) && (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)) {
        toggleMenu();
    }

    if (item.command) {
        item.command({ originalEvent: event, item: item });
    }

    // Toggle this folder's expanded state (keeps other folders open)
    if (item.items) {
        toggleMenuItem(itemKey.value);
    }

    setActiveMenuItem(itemKey.value);
}

function checkActiveRoute(item) {
    return route.path === item.to;
}


function hasPermission(item) {
    return canOpen(store.getters['auth/user'], item);
}

function hasVisibleChildren(item) {
    const user = store.getters['auth/user'];
    if (!item.items) return false;

    if (user && user.is_super_user && item.items.length > 0) {
      return true;
    }

    return item.items.some(child => hasPermission(child));
  }

// Transition hooks for smooth height animation
function onBeforeEnter(el) {
    el.style.height = '0';
    el.style.opacity = '0';
    el.style.overflow = 'hidden';
}

function onEnter(el, done) {
    el.style.transition = 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
    el.style.height = el.scrollHeight + 'px';
    el.style.opacity = '1';
    el.addEventListener('transitionend', function handler() {
        el.removeEventListener('transitionend', handler);
        el.style.height = '';
        el.style.overflow = '';
        done();
    });
}

function onBeforeLeave(el) {
    el.style.height = el.scrollHeight + 'px';
    el.style.opacity = '1';
    el.style.overflow = 'hidden';
}

function onLeave(el, done) {
    // Force reflow
    el.offsetHeight;
    el.style.transition = 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
    el.style.height = '0';
    el.style.opacity = '0';
    el.addEventListener('transitionend', function handler() {
        el.removeEventListener('transitionend', handler);
        done();
    });
}
</script>

<template>
		<template v-if="hasPermission(item)">
			<template v-if="'items' in item">
				<template v-if="hasVisibleChildren(item)">	
					<li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
							<router-link v-if="(!item.to || item.items) && item.visible !== false" @click="itemClick($event, item, index)" :class="[item.class, { 'active-route': checkActiveRoute(item) }]" tabindex="0" :to="{ name: item.target }">
									<i v-if="item.icon" :class="item.icon" class="layout-menuitem-icon"></i>
									<span class="layout-menuitem-text">{{ $t(item.label) }}</span>
									<span v-if="badgeCount > 0" class="menuitem-badge">{{ badgeCount }}</span>
									<i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
							</router-link>

							<!-- <router-link v-if="item.to && !item.items && item.visible !== false" @click="itemClick($event, item, index)" :class="[item.class, { 'active-route': checkActiveRoute(item) }]" tabindex="0" :to="{ name: item.target }">
									<i :class="item.icon" class="layout-menuitem-icon"></i>
									<span class="layout-menuitem-text">{{ $t(item.label) }}</span>
									<i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
							</router-link> -->
							<Transition v-if="item.items && item.visible !== false" :css="false" @before-enter="onBeforeEnter" @enter="onEnter" @before-leave="onBeforeLeave" @leave="onLeave">
									<ul v-show="root ? true : isActiveMenu" class="layout-submenu">
											<app-menu-item v-for="(child, i) in item.items" :key="child" :index="i" :item="child" :parentItemKey="itemKey" :root="false"></app-menu-item>
									</ul>
							</Transition>
					</li>
				</template>
			</template>
			<template v-else>
				<li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
							<router-link v-if="(!item.to || item.items) && item.visible !== false" @click="itemClick($event, item, index)" :class="[item.class, { 'active-route': checkActiveRoute(item) }]" tabindex="0" :to="{ name: item.target }">
									<i v-if="item.icon" :class="item.icon" class="layout-menuitem-icon"></i>
									<span class="layout-menuitem-text">{{ $t(item.label) }}</span>
									<span v-if="badgeCount > 0" class="menuitem-badge">{{ badgeCount }}</span>
									<i class="pi pi-fw pi-angle-down layout-submenu-toggler" v-if="item.items"></i>
							</router-link>
					</li>
			</template>		
	</template>
</template>

<style lang="scss" scoped>
.menuitem-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 18px;
	height: 18px;
	padding: 0 5px;
	margin-left: 6px;
	border-radius: 9px;
	background: var(--primary-color, #6366f1);
	color: #fff;
	font-size: 11px;
	font-weight: 600;
	line-height: 1;
}
</style>
